import {
  BeneficialOwnerControlType,
  OwnershipBeneficialOwner,
  Shareholding,
  useBusinessEntity,
  useOwnership,
  useStockClassList,
} from '@commenda/api';
import { Button, Dropdown, Icons, Loading } from '@commenda/kakashi';
import { NonTimezoneSensitiveDate } from '@commenda/utils';
import { useMemo, useState } from 'react';
import { BeneficialOwnersList } from './components/BeneficialOwnersList';
import { buildDisplayRows } from './components/buildDisplayRows';
import { buildStakeholderColorIndex } from './constants';
import { RemoveUboModal } from './components/RemoveUboModal';
import { DeleteStakeholderConfirm } from './components/DeleteStakeholderConfirm';
import { EditUltimateBeneficialOwnerControlTypeModal } from './components/EditUltimateBeneficialOwnerControlTypeModal';
import { ExceedsHundredBanner } from './components/ExceedsHundredBanner';
import { OwnershipEmptyState } from './components/OwnershipEmptyState';
import { RecentDocumentsCard } from './components/RecentDocumentsCard';
import { StakeholdersTable } from './components/StakeholdersTable';
import { SummaryBar } from './components/SummaryBar';
import { UploadSuccessModal } from './components/UploadSuccessModal';
import { useUploadCapTableDocs } from './useUploadCapTableDocs';
import { UploadProgressToast } from '../../document-views-v2/upload/UploadProgressToast';
import { isCapitalOnlyLlc, useEntityOwnershipNouns } from './ownershipNouns';

interface OwnershipTabProps {
  corporationId: number;
  companyId?: number;
  corporationName?: string;
  onAddIndividual: () => void;
  onAddEntity: () => void;
  onAddUbo: () => void;
  onEditShareClasses: () => void;
  onEditStakeholder: (stakeholder: Shareholding) => void;
  onViewKeyPerson: (directorId: number) => void;
  onViewDocuments?: () => void;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

export const OwnershipTab = ({
  corporationId,
  companyId,
  corporationName,
  onAddIndividual,
  onAddEntity,
  onAddUbo,
  onEditShareClasses,
  onEditStakeholder,
  onViewKeyPerson,
  onViewDocuments,
  onSuccess,
  onError,
}: OwnershipTabProps) => {
  const { data, isLoading, error } = useOwnership(corporationId, {
    offset: 0,
    limit: 500,
  });
  const { data: businessEntity } = useBusinessEntity(corporationId);
  const nouns = useEntityOwnershipNouns(corporationId);
  // Fetched for LLCs only — the kebab label has to agree with the screen it opens.
  const { data: tabStockClasses } = useStockClassList(
    nouns.tracksUnits ? corporationId : null
  );
  const capitalOnly = isCapitalOnlyLlc(nouns.tracksUnits, tabStockClasses);
  const [deletingStakeholder, setDeletingStakeholder] =
    useState<Shareholding>();
  const [removingUbo, setRemovingUbo] = useState<OwnershipBeneficialOwner>();
  const [editingUbo, setEditingUbo] = useState<{
    directorId: number;
    currentControlType: BeneficialOwnerControlType | null;
  }>();
  const [hoveredStakeholdingId, setHoveredStakeholdingId] =
    useState<string>();

  const upload = useUploadCapTableDocs({
    corporationId,
    companyId,
    onSuccess,
    onError,
  });
  const displayRows = useMemo(
    () =>
      data
        ? buildDisplayRows(
            data.shareholdings,
            data.totalOwnershipPercent,
            data.percentScale
          )
        : [],
    [data]
  );

  const colorIndexById = useMemo(
    () => buildStakeholderColorIndex(displayRows),
    [displayRows]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4xl">
        <Loading />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="flex items-center justify-center py-4xl">
        <p className="txt-small-regular text-text-tertiary">
          Couldn&apos;t load ownership data. Please refresh the page.
        </p>
      </div>
    );
  }

  if (businessEntity?.generalCorporationInfo.ownership?.isEnabled === false) {
    return (
      <div className="flex items-center justify-center py-4xl">
        <p className="txt-small-regular text-text-tertiary">
          Ownership isn&apos;t recorded for this type of entity.
        </p>
      </div>
    );
  }

  const isEmpty = data.shareholdings.length === 0;

  return (
    <div className="flex flex-col gap-2xl">
      <div className="flex items-start justify-between gap-md">
        <div className="flex flex-col gap-xs">
          <h2 className="txt-extra-large-bold text-text-primary">Ownership</h2>
          <p className="txt-extra-small-regular text-text-secondary">
            Significant {nouns.lowerPlural} of{' '}
            {data.entity.name ?? 'this entity'}
          </p>
        </div>
        <div className="flex items-center gap-md">
          <Dropdown
            menu={{
              groups: [
                {
                  items: [
                    {
                      label: 'Upload ownership documents',
                      icon: Icons.Upload01,
                      subMenu: {
                        groups: [
                          {
                            items: [
                              {
                                label: 'Upload files',
                                onClick: upload.openFilePicker,
                                icon: Icons.File04,
                              },
                              {
                                label: 'Upload folder',
                                onClick: upload.openFolderPicker,
                                icon: Icons.Folder,
                              },
                            ],
                          },
                        ],
                      },
                    },
                    {
                      label: `Edit ${(capitalOnly ? 'Capital' : nouns.sectionTitle).toLowerCase()}`,
                      onClick: onEditShareClasses,
                      icon: Icons.Edit02,
                    },
                  ],
                },
              ],
            }}
            align="end"
            sideOffset={8}
            contentClassName="!w-[250px]"
          >
            <Button
              variant="tertiary"
              size="md"
              aria-label="More actions"
              className="!px-md"
            >
              <Icons.DotsVertical className="size-4" />
            </Button>
          </Dropdown>
          <input
            ref={upload.fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={upload.handleFilesPicked}
          />
          <input
            ref={upload.folderInputRef}
            type="file"
            // @ts-expect-error -- webkitdirectory not in HTML spec but supported by browsers
            webkitdirectory=""
            className="hidden"
            onChange={upload.handleFilesPicked}
          />
          <Dropdown
            menu={{
              groups: [
                {
                  items: [
                    {
                      label: 'Individual',
                      onClick: onAddIndividual,
                      icon: Icons.User01,
                    },
                    {
                      label: 'Entity',
                      onClick: onAddEntity,
                      icon: Icons.Building01,
                    },
                  ],
                },
              ],
            }}
            align="end"
            sideOffset={8}
          >
            <Button variant="primary" size="md">
              <Icons.Plus className="size-4" />
              Add {nouns.lowerSingular}
            </Button>
          </Dropdown>
        </div>
      </div>

      <div className="flex gap-2xl">
        <div
          data-ai-section="Ownership structure"
          className="flex-1 flex flex-col gap-4xl min-w-0"
        >
          <ExceedsHundredBanner
            totalOwnershipPercent={data.totalOwnershipPercent}
            percentScale={data.percentScale}
          />
          {isEmpty ? (
            <OwnershipEmptyState nouns={nouns} />
          ) : (
            <div className="flex flex-col gap-xl">
              <SummaryBar
                shareholdings={displayRows}
                totalOwnershipPercent={data.totalOwnershipPercent}
                percentScale={data.percentScale}
                colorIndexById={colorIndexById}
                hoveredId={hoveredStakeholdingId}
                onHoverChange={setHoveredStakeholdingId}
              />
              {data.lastUpdatedAt && (
                <p className="italic txt-extra-small-regular text-text-tertiary px-xl">
                  Last updated{' '}
                  {NonTimezoneSensitiveDate.fromISO8601String(
                    data.lastUpdatedAt.slice(0, 10)
                  ).format('MMM D, YYYY')}
                </p>
              )}
              <StakeholdersTable
                rows={displayRows}
                nounSingular={nouns.singular}
                classNounSingular={nouns.classNouns?.singular ?? null}
                percentScale={data.percentScale}
                colorIndexById={colorIndexById}
                onEdit={onEditStakeholder}
                onDelete={setDeletingStakeholder}
                onRowHoverChange={setHoveredStakeholdingId}
                hoveredStakeholdingId={hoveredStakeholdingId}
              />
            </div>
          )}
        </div>

        <aside className="w-[326px] shrink-0 flex flex-col gap-lg">
          <div data-ai-section="Ultimate beneficial owners">
            <BeneficialOwnersList
              beneficialOwners={data.beneficialOwners}
              onViewKeyPerson={onViewKeyPerson}
              onEditControlType={(bor) =>
                setEditingUbo({
                  directorId: bor.directorId,
                  currentControlType:
                    bor.directControlType ?? bor.paths[0]?.controlType ?? null,
                })
              }
              onRemove={setRemovingUbo}
              onAdd={onAddUbo}
            />
          </div>
          <div data-ai-section="Recent ownership documents">
            <RecentDocumentsCard documents={data.recentDocuments} />
          </div>
        </aside>
      </div>

      <DeleteStakeholderConfirm
        isOpen={deletingStakeholder !== undefined}
        onClose={() => setDeletingStakeholder(undefined)}
        corporationId={corporationId}
        stakeholder={deletingStakeholder}
        onSuccess={onSuccess}
        onError={onError}
      />

      {removingUbo && (
        <RemoveUboModal
          onClose={() => setRemovingUbo(undefined)}
          corporationId={corporationId}
          beneficialOwner={removingUbo}
          onSuccess={onSuccess}
          onError={onError}
        />
      )}

      <EditUltimateBeneficialOwnerControlTypeModal
        isOpen={editingUbo !== undefined}
        onClose={() => setEditingUbo(undefined)}
        corporationId={corporationId}
        directorId={editingUbo?.directorId}
        currentControlType={editingUbo?.currentControlType}
        onSuccess={onSuccess}
        onError={onError}
      />

      <UploadProgressToast
        isUploading={upload.uploadState.isUploading}
        totalFiles={upload.uploadState.totalFiles}
        completedFiles={upload.uploadState.completedFiles}
      />

      <UploadSuccessModal
        isOpen={upload.uploadSuccess?.show ?? false}
        onClose={upload.dismissUploadSuccess}
        corporationName={corporationName ?? data.entity.name ?? undefined}
        fiscalYear={upload.uploadSuccess?.fiscalYear ?? new Date().getFullYear()}
        onViewDocuments={() => {
          upload.dismissUploadSuccess();
          onViewDocuments?.();
        }}
      />
    </div>
  );
};
